import { VStack } from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useShowDatasourceWebhookConfiguration } from 'src/api/queries/microservices/platform-markets-stonex-config'
import { Box, Button, CheckCircleIcon, CopyIcon, Dialog, Row, Skeleton, Text } from 'src/ui'
import { useToast } from 'src/utils/toast'

type StoneXWebhookConfigurationDialogProps = {
  isOpen: boolean
  onClose: () => void
}

export default function StoneXWebhookConfigurationDialog({
  isOpen,
  onClose,
}: Readonly<StoneXWebhookConfigurationDialogProps>) {
  const { data: webhookConfig, isLoading } = useShowDatasourceWebhookConfiguration(isOpen)

  const toast = useToast()
  const [copiedField, setCopiedField] = React.useState<'secret' | 'url' | undefined>(undefined)
  const copiedFieldTimeoutRef = useRef<NodeJS.Timeout | undefined>()

  const copyToClipboard = async (value: string, type: 'url' | 'secret') => {
    clearTimeout(copiedFieldTimeoutRef.current)

    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(type)
      copiedFieldTimeoutRef.current = setTimeout(() => {
        setCopiedField(undefined)
      }, 2000)
    } catch {
      toast({ status: 'error', title: 'Error', description: 'Failed to copy to clipboard' })
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="StoneX webhook configuration"
      size="lg"
      showCloseButton
    >
      <VStack gap={2} w="full" alignItems="flex-start">
        <CopyableValue
          label="Webhook URL"
          value={webhookConfig?.webhookUrl}
          isLoading={isLoading}
          isCopied={copiedField === 'url'}
          onCopy={(value) => copyToClipboard(value, 'url')}
        />

        <CopyableValue
          label="Webhook Secret"
          value={webhookConfig?.webhookSecret}
          isLoading={isLoading}
          isCopied={copiedField === 'secret'}
          onCopy={(value) => copyToClipboard(value, 'secret')}
        />
      </VStack>
    </Dialog>
  )
}

type CopyableValueProps = Readonly<{
  label: string
  value?: string
  isLoading: boolean
  isCopied: boolean
  onCopy: (value: string) => void
}>

function CopyableValue({ label, value, isLoading, isCopied, onCopy }: CopyableValueProps) {
  return (
    <>
      <Row alignItems="center" justifyContent="space-between" mt={4} mb={1} w="full">
        <Text fontWeight="bold">{label}</Text>
        {!isLoading && (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={isCopied ? <CheckCircleIcon /> : <CopyIcon />}
            onClick={() => onCopy(value)}
            isDisabled={!value}
          >
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </Row>
      <Skeleton isLoaded={!isLoading} w="full">
        <Box borderWidth="1px" borderRadius="md" bg="gray.50" p={3}>
          <Text whiteSpace="pre-wrap" wordBreak="break-word" fontFamily="mono" fontSize="sm">
            {value ?? ''}
          </Text>
        </Box>
      </Skeleton>
    </>
  )
}
